/**
 * Notification Type Registry
 *
 * Central configuration for notification types across the application.
 * Each type defines its icon, colors, priority, and optional behaviors.
 *
 * This eliminates duplicated switch statements in NotificationToastStack,
 * NotificationList, and other notification-related components.
 *
 * To add a new notification type:
 * 1. Add an entry to NOTIFICATION_TYPE_REGISTRY in notificationRegistry.ts
 * 2. Use getNotificationConfig(type) to retrieve the config
 */

import type { NotificationToastItem } from '../store/notificationStore';
import { NOTIFICATION_TYPE_REGISTRY, type NotificationType } from './notificationRegistry';

export interface NotificationTypeConfig {
  /** FontAwesome icon class (e.g., 'fa-wrench') - just the icon name, not full class */
  icon: string;

  /** Toast background color (Tailwind class, e.g., 'bg-green-600') */
  toastBg: string;

  /** Toast hover background — a complete `hover:bg-*` class, e.g. 'hover:bg-green-700'.
   *  Storing the full class is required: Tailwind cannot see one built at runtime. */
  toastHoverBg: string;

  /** List icon background color (Tailwind class, e.g., 'bg-green-100') */
  listIconBg: string;

  /** List icon text color (Tailwind class, e.g., 'text-green-600') */
  listIconText: string;

  /** Priority: 'low' | 'normal' | 'high' | 'urgent' */
  priority: 'low' | 'normal' | 'high' | 'urgent';

  /** Auto-dismiss after X ms (0 = never, default = 0) */
  autoDismissMs: number;

  /** Group key - toasts with same key can be batched */
  groupKey?: string;

  /** Optional action buttons for the toast */
  actions?: Array<{
    label: string;
    icon?: string;
    variant: 'primary' | 'secondary' | 'danger';
    onClick: (toast: NotificationToastItem) => void;
  }>;

  /** Fully custom render function (overrides default layout) */
  render?: (toast: NotificationToastItem, onDismiss: () => void) => React.ReactNode;
}

/**
 * Default configuration applied when a type is not found in the registry
 */
const DEFAULT_CONFIG: NotificationTypeConfig = {
  icon: 'fa-bell',
  toastBg: 'bg-chrome-muted',
  toastHoverBg: 'hover:bg-chrome',
  listIconBg: 'bg-surface-inset',
  listIconText: 'text-ink-body',
  priority: 'normal',
  autoDismissMs: 0,
};

/**
 * Runtime overrides for function-valued fields that can't live in `as const`.
 * Written to by registerNotificationType().
 */
const RUNTIME_OVERRIDES = new Map<string, Partial<NotificationTypeConfig>>();

/**
 * Get the full configuration for a notification type
 *
 * Falls back to DEFAULT_CONFIG for any missing properties.
 * Merges: DEFAULT_CONFIG < registry static data < runtime overrides
 *
 * @param type - The notification type string
 * @returns Complete notification type configuration
 */
export const getNotificationConfig = (type: string): NotificationTypeConfig => {
  const registryEntry = NOTIFICATION_TYPE_REGISTRY[type as NotificationType];
  const staticConfig: Partial<NotificationTypeConfig> = registryEntry
    ? {
        icon: registryEntry.icon,
        toastBg: registryEntry.toastBg,
        toastHoverBg: registryEntry.toastHoverBg,
        listIconBg: registryEntry.listIconBg,
        listIconText: registryEntry.listIconText,
        priority: registryEntry.priority,
      }
    : {};
  const runtimeOverride = RUNTIME_OVERRIDES.get(type) || {};
  return { ...DEFAULT_CONFIG, ...staticConfig, ...runtimeOverride };
};

/**
 * Register a new notification type at runtime
 *
 * Useful for:
 * - Feature flags that conditionally add notification types
 * - Modular features that register their own types on load
 * - Testing with custom notification types
 *
 * @param type - The notification type string
 * @param config - Partial configuration (merged with defaults)
 */
export const registerNotificationType = (
  type: string,
  config: Partial<NotificationTypeConfig>
): void => {
  const existing = RUNTIME_OVERRIDES.get(type) || {};
  RUNTIME_OVERRIDES.set(type, { ...existing, ...config });
};

/**
 * Debug mode flag - enables verbose logging in notification components
 *
 * Enabled automatically in development, or manually via localStorage:
 *   localStorage.setItem('NOTIFICATION_DEBUG', 'true')
 */
const NOTIFICATION_DEBUG =
  (typeof import.meta !== 'undefined' && import.meta.env?.DEV) ||
  (typeof localStorage !== 'undefined' && localStorage.getItem('NOTIFICATION_DEBUG') === 'true');

/**
 * Notification logger for debugging
 *
 * Provides consistent logging format across notification components.
 * Only logs in debug mode (except for warn/error which always log).
 */
export const notificationLogger = {
  log: (...args: unknown[]) => NOTIFICATION_DEBUG && console.log('[Notification]', ...args),
  warn: (...args: unknown[]) => console.warn('[Notification]', ...args),
  error: (...args: unknown[]) => console.error('[Notification]', ...args),

  /** Structured event logging for telemetry/debugging */
  event: (event: string, data?: unknown) => {
    if (NOTIFICATION_DEBUG) {
      console.log(`[Notification:${event}]`, data);
    }
  },
};
